import { format, formatDistanceToNow, isValid, parse, parseISO } from "date-fns";
import { es } from "date-fns/locale";

/**
 * Smart date parsing function that detects ISO date format (YYYY-MM-DD)
 * and uses parseISO to avoid timezone issues, otherwise uses new Date()
 */
function smartParseDate(date: Date | string | number): Date {
  if (date instanceof Date) return date;
  
  if (typeof date === "string") {
    // Detect ISO date-only format (YYYY-MM-DD) from backend
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      return parseISO(date); // Use parseISO for backend dates to avoid timezone issues
    }
    // For other formats (with time, timezone, etc)
    return new Date(date);
  }
  
  // For numbers (timestamps)
  return new Date(date);
}

/**
 * Devuelve la fecha actual en formato YYYY-MM-DD para inputs HTML tipo date.
 * 
 * Usa la zona horaria local del navegador (Argentina: UTC-3).
 * NO usa UTC para evitar diferencias de ±1 día cerca de la medianoche.
 * 
 * Formato: ISO 8601 date-only (requerido por <input type="date">)
 */
export function getTodayDateString(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Formats a date to DD/MM/YYYY format
 */
export function formatDateAR(date: Date | string | number): string {
  if (!date) return "";
  const dateObj = smartParseDate(date);
  if (!isValid(dateObj)) return "";
  return format(dateObj, "dd/MM/yyyy");
}

/**
 * Formats a date to DD/MM/YYYY HH:mm format
 */
export function formatDateTimeAR(date: Date | string | number): string {
  if (!date) return "";
  const dateObj = smartParseDate(date);
  if (!isValid(dateObj)) return "";
  return format(dateObj, "dd/MM/yyyy HH:mm");
}

/**
 * Formats a date using locale-specific formatting
 */
export function formatDateLocale(
  date: Date | string | number,
  localeFormat = "dd/MM/yyyy"
): string {
  if (!date) return "";
  const dateObj = smartParseDate(date);
  if (!isValid(dateObj)) return "";
  return format(dateObj, localeFormat, { locale: es });
}

/**
 * Formats validity date from DD/MM/YYYY format to "DD MMM" format
 * Handles both DD/MM/YYYY and ISO date formats
 */
export function formatValidityDate(dateString?: string): string | null {
  if (!dateString) return null;

  // Primary path: Backend sends DD/MM/YYYY format
  if (dateString.includes("/")) {
    const parts = dateString.split("/");
    if (parts.length !== 3) return null; // Ensure we have day/month/year

    const [day, month] = parts;
    const monthNames = [
      "ene", "feb", "mar", "abr", "may", "jun",
      "jul", "ago", "sep", "oct", "nov", "dic"
    ];

    const monthIndex = parseInt(month) - 1;
    // Validate month is within valid range (0-11)
    if (monthIndex < 0 || monthIndex > 11) return null;

    return `${day} ${monthNames[monthIndex]}`;
  }

  // Fallback: Try parsing ISO format if it comes directly
  const date = smartParseDate(dateString);
  if (!isValid(date)) return null;

  return format(date, "d MMM", { locale: es });
}

/**
 * Parses a DD/MM/YYYY date string and returns a Date object
 */
export function parseDateAR(dateString: string): Date | null {
  if (!dateString) return null;

  try {
    const parsed = parse(dateString, "dd/MM/yyyy", new Date());
    return isValid(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Parses a numeric input value that may contain commas as decimal separators.
 * Converts Spanish/Argentine number format (comma as decimal) to standard format.
 * 
 * @param value - The input value as string or number
 * @returns The parsed number, or 0 if the input is empty
 * 
 * @example
 * parseNumericInput("123,45") // returns 123.45
 * parseNumericInput("123.45") // returns 123.45
 * parseNumericInput(123.45) // returns 123.45
 * parseNumericInput("") // returns 0
 */
export function parseNumericInput(value: string | number): number {
  const raw = String(value).replace(",", ".").trim();
  return raw === "" ? 0 : Number(raw);
}

/**
 * Normalizes a numeric string by replacing comma with dot.
 * Returns the parsed number if valid, or the original value if not.
 *
 * @param value - The input string to normalize
 * @returns The parsed number if valid, otherwise the original value
 */
export function normalizeNumericString(value: string): number | string {
  const normalized = value.replace(",", ".").trim();
  if (normalized === "") return "";
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : value;
}

/**
 * Formats a YYYY-MM string to "MMM yyyy" format in Spanish
 * @example
 * formatMonthYear("2025-01") // returns "ene 2025"
 * formatMonthYear("2025-11") // returns "nov 2025"
 */
export function formatMonthYear(yearMonth: string): string {
  if (!yearMonth) return "";
  const date = parseISO(`${yearMonth}-01`);
  if (!isValid(date)) return yearMonth;
  return format(date, "MMM yyyy", { locale: es });
}

/**
 * Formats a date to short month and day in Spanish
 * @example
 * formatShortDate(new Date()) // returns "15 nov"
 */
export function formatShortDate(date: Date | string | number): string {
  if (!date) return "";
  const dateObj = smartParseDate(date);
  if (!isValid(dateObj)) return "";
  return format(dateObj, "d MMM", { locale: es });
}

/**
 * Formats a date to a relative time string in Spanish.
 * @example
 * formatRelativeTimeAR("2025-01-10T12:00:00Z") // "hace 5 minutos"
 */
export function formatRelativeTimeAR(date: Date | string | number): string {
  if (!date) return "";
  const dateObj = smartParseDate(date);
  if (!isValid(dateObj)) return "";
  return formatDistanceToNow(dateObj, { locale: es, addSuffix: true });
}
